import { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { LuCpu } from 'react-icons/lu';
import { parseJobTime } from './ProjectCards';

/**
 * Whether a GPU machine is starting or working for this video: the analysis, a run again, or following the objects a
 * person added. It always sits in the same place, the right end of the breadcrumb row, and is gone while no machine
 * is needed (removing, merging and the assistant run without one).
 */

const tk = ({ theme: t }) => t.token;

const n = k => (k === 1 ? '1 added object' : `${k} added objects`);

/**
 * { state: 'waiting' | 'starting' | 'working', text, since } or null. `job` is the analysis shown (a run again is
 * one), `follow` the corrections layer's follow state, `following` how many added objects wait to be followed.
 */
export const gpuStatus = ({ job, phase, follow, following = 0 }) => {
	if (phase === 'waiting')
		return {
			state: 'waiting',
			text: 'Waiting for a free GPU machine',
			since: job?.capacity_waiting_since || job?.created_at,
		};
	if (phase === 'running')
		return (Number(job?.progress) || 0) < 5
			? { state: 'starting', text: 'Starting a GPU machine', since: job?.created_at }
			: { state: 'working', text: 'GPU machine working · analyzing' };
	if (follow?.status === 'starting')
		return {
			state: 'starting',
			text: `Starting a GPU machine · to follow ${n(following || 1)}`,
			since: follow.since,
		};
	if (follow?.status === 'following')
		return { state: 'working', text: `GPU machine working · following ${n(following || 1)}` };
	return null;
};

const elapsed = iso => {
	const d = iso ? parseJobTime(iso) : null;
	if (!d || Number.isNaN(d.getTime())) return null;
	const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
	return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

const breathe = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.3; }
`;

const Pill = styled.div`
	margin-left: auto;
	flex: none;
	display: inline-flex;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	height: ${p => tk(p).controlHeightSM}px;
	padding: 0 ${p => tk(p).paddingSM}px;
	border-radius: ${p => tk(p).controlHeightSM}px;
	border: ${p =>
		`${tk(p).lineWidth}px ${tk(p).lineType} ${p.$state === 'working' ? tk(p).colorBrandDark : tk(p).colorWarning}`};
	background: ${p => tk(p).panelGlassBg};
	color: ${p => tk(p).colorTextLightSolid};
	font-size: ${p => tk(p).fontSizeSM}px;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	svg {
		flex: none;
		color: ${p => (p.$state === 'working' ? tk(p).colorBrandLight : tk(p).colorWarning)};
		${p =>
			p.$state !== 'working' &&
			css`
				animation: ${breathe} 1.4s ease-in-out infinite;
			`}
	}
	small {
		color: ${p => tk(p).colorTextTertiary};
		font-size: ${p => tk(p).fontSizeSM}px;
	}
	@media (prefers-reduced-motion: reduce) {
		svg {
			animation: none;
		}
	}
`;

const GpuStatus = ({ status }) => {
	const [, setTick] = useState(0);
	const ticking = !!status?.since;
	useEffect(() => {
		if (!ticking) return undefined;
		const id = setInterval(() => setTick(t => t + 1), 1000);
		return () => clearInterval(id);
	}, [ticking]);
	if (!status) return null;
	const clock = elapsed(status.since);
	// only the words are announced, when they change; the clock ticks every second outside the live region, still
	// there to be read
	return (
		<Pill $state={status.state}>
			<LuCpu aria-hidden="true" />
			<span role="status">{status.text}</span>
			{clock && <small>{clock}</small>}
		</Pill>
	);
};

export default GpuStatus;
